import { useState, useEffect } from 'react';
import { fmtMoney, fmtMoneyShort, fmtNumber } from '@/lib/format';
import { useSearchParams, Link } from 'react-router-dom';
import { MetricCard, SectionHeader, PremiumTable, StatusBadge, EmptyState, PageSkeleton } from '@/components/dashboard/MetricCard';
import { MembershipCard } from '@/components/dashboard/MembershipCard';
import { LifetimeUpgradeBanner } from '@/components/dashboard/LifetimeUpgrade';
import { ReferralLinksCard } from '@/components/dashboard/ReferralLinksCard';
import { MembershipStatusCard } from '@/components/dashboard/MembershipStatusCard';
import { useAccess, TIER_LABEL } from '@/components/dashboard/access';
import { useDashboard } from '@/context/DashboardContext';
import { useCopy } from '@/context/CopyContext';
import { dashboardApi } from '@/services/dashboardApi';
import { DollarSign, HandCoins, TrendingUp, Users, Sparkles, GraduationCap, Building2, CalendarDays, Lock, ArrowRight } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { motion } from 'framer-motion';
import { toast } from 'sonner';

const CustomTooltip = ({ active, payload, label, cur }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#0d1829] border border-white/[0.08] rounded-xl px-4 py-3 shadow-2xl">
      <p className="text-[11px] text-gray-400 uppercase tracking-wider mb-1">{label}</p>
      <p className="text-white font-bold">{fmtMoney(payload[0].value, cur)}</p>
    </div>
  );
};

/*
  Inicio según el nivel (decisión de Samuel, 09/10/2026):
  - free (visitante): bienvenida, invitación a empezar por el Bloque 0 de la Academia y tarjetas de membresía.
  - Access / PRO (miembro): estado de su membresía, accesos rápidos, enlace de invitación y upgrade.
  - Premium (broker) y equipo: el panel de broker con sus métricas.
*/

// Acceso rápido (Oportunidades, Academia, Eventos…)
function QuickLink({ to, icon: Icon, title, text, locked = false, delay = 0 }) {
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay }}>
      <Link to={to}
        className="group flex h-full min-h-[72px] items-start gap-3 rounded-2xl border border-white/[0.06] bg-white/[0.02] p-5 hover:border-[#c9a84c]/30 transition-colors">
        <span className="w-9 h-9 rounded-xl bg-[#c9a84c]/10 flex items-center justify-center shrink-0">
          <Icon size={17} className="text-[#c9a84c]" aria-hidden="true" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-1.5 text-sm text-white font-medium">
            {title}
            {locked && <Lock size={11} className="text-[#c9a84c]/70" aria-label="Solo miembros" />}
          </span>
          <span className="block text-xs text-gray-400 mt-1 leading-relaxed">{text}</span>
        </span>
        <ArrowRight size={15} className="text-gray-500 group-hover:text-[#c9a84c] transition-colors mt-1 shrink-0" aria-hidden="true" />
      </Link>
    </motion.div>
  );
}

export default function DashboardHome() {
  const { user, membership, membershipStatus, refreshMembership } = useDashboard();
  const access = useAccess();
  const broker = access.isBroker;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [plans, setPlans] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();

  // Editable copy
  const greeting = useCopy('home.greeting_prefix');
  const subtitle = useCopy('home.subtitle');
  const errorText = useCopy('home.error');
  const salesLabel = useCopy('home.metric_sales_label');
  const salesSub = useCopy('home.metric_sales_sub');
  const commissionsLabel = useCopy('home.metric_commissions_label');
  const commissionsSub = useCopy('home.metric_commissions_sub');
  const contributionsLabel = useCopy('home.metric_contributions_label');
  const contributionsSub = useCopy('home.metric_contributions_sub');
  const clientsLabel = useCopy('home.metric_clients_label');
  const clientsSub = useCopy('home.metric_clients_sub');
  const membershipsTitle = useCopy('home.memberships_title');
  const membershipsBadge = useCopy('home.memberships_badge');
  const chartTitle = useCopy('home.chart_title');
  const recentSalesTitle = useCopy('home.recent_sales_title');
  const colClient = useCopy('home.table_client');
  const colAmount = useCopy('home.table_amount');
  const colStatus = useCopy('home.table_status');

  // Métricas solo para brokers Premium y equipo (el resto no las tiene: el servidor las devuelve vacías)
  useEffect(() => {
    if (!broker) { setData(null); setLoading(false); return; }
    setLoading(true);
    dashboardApi.metrics()
      .then(setData)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [broker]);

  useEffect(() => {
    dashboardApi.membershipPlans().then(r => setPlans(r.plans || [])).catch(console.error);
  }, []);

  // Maneja el retorno de Stripe Checkout (success/cancel)
  useEffect(() => {
    const purchase = searchParams.get('purchase');
    if (!purchase) return;

    if (purchase === 'success') {
      const sessionId = searchParams.get('session_id');
      if (sessionId) {
        dashboardApi.getCheckoutSession(sessionId)
          .then(session => {
            if (session.status === 'paid') {
              toast.success('¡Membresía activada correctamente!');
              refreshMembership();
            } else {
              toast.info('Procesando tu pago... puede tardar unos segundos.');
              // El webhook puede tardar: reintenta varias veces (así aparece el pop-up de vitalicia)
              [3000, 7000, 12000, 20000].forEach((ms) => setTimeout(refreshMembership, ms));
            }
          })
          .catch(() => toast.error('No se pudo verificar el pago'));
      }
    } else if (purchase === 'cancel') {
      toast.info('Pago cancelado');
    }

    // Limpia query params para evitar re-trigger
    setSearchParams({}, { replace: true });
  }, [searchParams, setSearchParams, refreshMembership]);

  // Retorno del pago de la plaza vitalicia
  useEffect(() => {
    const lifetime = searchParams.get('lifetime');
    if (!lifetime) return;
    if (lifetime === 'success') {
      toast.success('¡Pago recibido! Tu plaza vitalicia se activará en unos segundos.');
      [2000, 6000, 12000].forEach((ms) => setTimeout(refreshMembership, ms));
    } else if (lifetime === 'cancel') {
      toast.info('Pago cancelado');
      refreshMembership();
    }
    setSearchParams({}, { replace: true });
  }, [searchParams, setSearchParams, refreshMembership]);

  // Enlace «Hazte socio» desde secciones bloqueadas: baja hasta las membresías
  useEffect(() => {
    if (!loading && plans.length && membershipStatus === 'ready' && window.location.hash === '#membresias') {
      document.getElementById('membresias')?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [loading, plans.length, membershipStatus]);

  if (access.loading || (broker && loading)) return <PageSkeleton cards={4} rows={5} />;

  const currentMembership = membership?.active_membership;
  // Solo mostramos planes superiores al actual (sin plan → todos; Premium → ninguno)
  const RANK = { starter: 1, basica: 2, premium: 3 };
  const upgradePlans = plans.filter(p => (RANK[p.id] || 0) > (RANK[currentMembership] || 0));
  const hasActivity = !!data && ((data.recent_sales?.length > 0) || (data.monthly || []).some(m => Number(m.ventas) > 0));
  const cur = data?.currency || 'usd';
  const firstName = user?.name?.split(' ')[0] || '';

  const plansSection = (title) => membershipStatus === 'ready' && upgradePlans.length > 0 && (
    <section id="membresias" className="scroll-mt-24">
      <div className="flex flex-col sm:flex-row sm:items-baseline justify-between gap-1 mb-4">
        <h3 className="text-[11px] text-gray-400 uppercase tracking-wider font-semibold">{title}</h3>
        <span className="text-[11px] text-[#c9a84c]">{membershipsBadge}</span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {upgradePlans.map((plan, i) => (
          <MembershipCard
            key={plan.id}
            plan={plan}
            currentMembership={currentMembership}
            currency={membership?.currency}
            upgrade={membership?.upgrades?.[plan.id] || null}
            delay={0.1 + i * 0.1}
          />
        ))}
      </div>
    </section>
  );

  // ── Inicio «visitante» (free) ──
  if (!access.isMember) {
    return (
      <div className="space-y-8" data-testid="dashboard-home-free">
        <SectionHeader title={`Te damos la bienvenida, ${firstName}`} subtitle="Ya tienes tu cuenta en Freedom Homes Club. Este es un buen punto de partida." />

        <motion.section
          initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}
          className="rounded-2xl border border-[#c9a84c]/20 bg-gradient-to-br from-[#c9a84c]/[0.07] to-white/[0.02] p-6 sm:p-8"
          data-testid="home-start-block0"
        >
          <p className="text-[11px] text-[#c9a84c] uppercase tracking-wider font-semibold mb-2">Empieza por aquí</p>
          <h3 className="text-white text-xl font-semibold mb-2">Bloque 0 de la Academia</h3>
          <p className="text-sm text-gray-400 leading-relaxed max-w-2xl mb-6">
            Qué es el club, cómo funciona por dentro y cómo sacarle partido desde el primer día. Es gratuito
            y te ayudará a decidir con calma qué membresía encaja contigo.
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <Link to="/dashboard/academia"
              className="inline-flex items-center justify-center gap-2 min-h-[44px] rounded-xl bg-[#c9a84c] px-5 text-sm font-semibold text-[#0a1628] hover:bg-[#d8b95d] transition-colors">
              <GraduationCap size={16} /> Empezar el Bloque 0
            </Link>
            <Link to="/dashboard/eventos"
              className="inline-flex items-center justify-center gap-2 min-h-[44px] rounded-xl border border-white/10 px-5 text-sm text-gray-300 hover:text-white hover:border-[#c9a84c]/40 transition-colors">
              <CalendarDays size={16} /> Ver eventos y bootcamp
            </Link>
          </div>
        </motion.section>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <QuickLink to="/dashboard/academia" icon={GraduationCap} title="Academia" text="Formación del club, a tu ritmo." delay={0.05} />
          <QuickLink to="/dashboard/eventos" icon={CalendarDays} title="Eventos y bootcamp" text="Encuentros presenciales y en línea." delay={0.1} />
          <QuickLink to="/dashboard/propiedades" icon={Building2} title="Oportunidades" text="Las operaciones del club, para miembros." locked delay={0.15} />
        </div>

        {plansSection('Elige tu membresía')}
      </div>
    );
  }

  // ── Inicio «miembro» (Access / PRO) ──
  if (!broker) {
    return (
      <div className="space-y-8" data-testid="dashboard-home-member">
        <SectionHeader title={`${greeting} ${firstName}`} subtitle={`Tu espacio como miembro ${TIER_LABEL[access.tier] || ''} de Freedom Homes Club`} />

        <LifetimeUpgradeBanner />

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
          <MembershipStatusCard />
          <div className="grid grid-cols-1 gap-3">
            <QuickLink to="/dashboard/propiedades" icon={Building2} title="Oportunidades" text="Las operaciones que se presentan a la comunidad." delay={0.05} />
            <QuickLink to="/dashboard/academia" icon={GraduationCap} title="Academia" text="Tu formación dentro del club." delay={0.1} />
            <QuickLink to="/dashboard/eventos" icon={CalendarDays} title="Eventos y bootcamp" text="Próximos encuentros del club." delay={0.15} />
          </div>
        </div>

        <ReferralLinksCard refCode={user?.referral_code || user?.referralCode} variant="compact" delay={0.2} />

        {plansSection(membershipsTitle)}
      </div>
    );
  }

  // ── Inicio de broker (Premium) y equipo ──
  return (
    <div className="space-y-8" data-testid="dashboard-home">
      <SectionHeader title={`${greeting} ${firstName}`} subtitle={subtitle} />

      <ReferralLinksCard refCode={user?.referral_code || user?.referralCode} variant="compact" delay={0.05} />

      {data ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <MetricCard label={salesLabel} value={`${fmtMoney(data.total_sales_amount, cur)}`} sub={`${fmtNumber(data.pending_count ?? 0)} ${salesSub}`} icon={DollarSign} delay={0} />
          <MetricCard label={commissionsLabel} value={`${fmtMoney(data.total_commissions, cur)}`} sub={data.commissions_pending > 0 ? `${commissionsSub} · ${fmtMoney(data.commissions_pending, cur)} por cobrar` : commissionsSub} icon={TrendingUp} delay={1} />
          <MetricCard label={contributionsLabel} value={fmtMoney(data.total_contributions ?? 0, cur)} sub={`${fmtNumber(data.contributions_count ?? 0)} ${contributionsSub}`} icon={HandCoins} delay={2} />
          <MetricCard label={clientsLabel} value={fmtNumber(data.total_clients ?? 0)} sub={clientsSub} icon={Users} delay={3} />
        </div>
      ) : (
        <p role="alert" className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-5 text-sm text-gray-400" data-testid="home-metrics-error">{errorText}</p>
      )}

      <LifetimeUpgradeBanner />

      {plansSection(membershipsTitle)}

      {data && !hasActivity && (
        <EmptyState
          icon={Sparkles}
          title="Aún no hay actividad registrada"
          text="Cuando tus contactos se registren y compren con tu enlace de invitación, aquí verás la evolución de ventas y tus comisiones. Empieza compartiendo tu enlace y repasando la formación de la Academia."
          action={
            <Link to="/dashboard/academia" className="inline-flex items-center gap-2 rounded-xl bg-[#c9a84c] px-4 py-2 text-sm font-semibold text-[#0a1628] hover:bg-[#d8b95d] transition-colors">
              <GraduationCap size={16} /> Ir a la Academia
            </Link>
          }
        />
      )}

      {hasActivity && data.monthly?.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.3 }}
          className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-6"
        >
          <h3 className="text-[11px] text-gray-400 uppercase tracking-wider font-semibold mb-5">{chartTitle}</h3>
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={data.monthly}>
              <defs>
                <linearGradient id="goldGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#c9a84c" stopOpacity={0.2} />
                  <stop offset="100%" stopColor="#c9a84c" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.03)" />
              <XAxis dataKey="month" tick={{ fill: '#4b5563', fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: '#4b5563', fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={(v) => fmtMoneyShort(v, cur)} />
              <Tooltip content={<CustomTooltip cur={cur} />} />
              <Area type="monotone" dataKey="ventas" stroke="#c9a84c" fill="url(#goldGrad)" strokeWidth={2} dot={false} activeDot={{ r: 4, fill: '#c9a84c', stroke: '#0a1628', strokeWidth: 2 }} />
            </AreaChart>
          </ResponsiveContainer>
        </motion.div>
      )}

      {data?.recent_sales?.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.4 }}
        >
          <h3 className="text-[11px] text-gray-400 uppercase tracking-wider font-semibold mb-4">{recentSalesTitle}</h3>
          <PremiumTable
            cols={[colClient, 'Operación', colAmount, colStatus]}
            rows={data.recent_sales.map(s => [
              s.client,
              s.kind === 'aportacion' ? <span className="text-[#c9a84c]">Aportación</span> : (s.property_name || '—'),
              `${fmtMoney(s.amount, s.currency || cur)}`,
              <StatusBadge status={s.status} />
            ])}
          />
        </motion.div>
      )}
    </div>
  );
}
